import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authApi } from '@/services/authApi';
import AuthShell from './AuthShell';
import s from './Auth.module.scss';

const OTP_SECONDS = 180;
function pwOk(pw: string) {
	if (pw.length < 8 || pw.length > 20) return false;
	let l = false, d = false, sp = false;
	for (const c of pw) { if (/[a-zA-Z]/.test(c)) l = true; else if (/[0-9]/.test(c)) d = true; else sp = true; }
	return l && d && sp;
}
const mmss = (sec: number) => `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;

/** [2026-09-10] 아이디 찾기 / 비밀번호 재설정 — 휴대폰 OTP(FIND_ID/RESET_PW) 재사용. */
export default function FindAccount() {
	const nav = useNavigate();
	const [tab, setTab] = useState<'findId' | 'resetPw'>('findId');
	const purpose = tab === 'findId' ? 'FIND_ID' : 'RESET_PW';

	const [phone, setPhone] = useState('');
	const [otpSent, setOtpSent] = useState(false);
	const [code, setCode] = useState('');
	const [verified, setVerified] = useState(false);
	const [timer, setTimer] = useState(0);
	const [msg, setMsg] = useState('');
	// 결과
	const [accounts, setAccounts] = useState<{ type: string; loginId?: string; social?: string }[] | null>(null);
	// 재설정
	const [loginId, setLoginId] = useState('');
	const [pw, setPw] = useState('');
	const [pw2, setPw2] = useState('');
	const [err, setErr] = useState<string | null>(null);
	const [done, setDone] = useState(false);

	useEffect(() => {
		if (!otpSent || verified || timer <= 0) return;
		const id = setInterval(() => setTimer((v) => (v <= 1 ? 0 : v - 1)), 1000);
		return () => clearInterval(id);
	}, [otpSent, verified, timer]);

	function switchTab(next: 'findId' | 'resetPw') {
		setTab(next); setOtpSent(false); setVerified(false); setCode(''); setTimer(0);
		setMsg(''); setAccounts(null); setErr(null); setDone(false);
	}

	async function sendOtp() {
		setMsg('');
		const r = await authApi.phoneSend(phone, purpose);
		if (!r.success) { setMsg(r.message || '발송 실패'); return; }
		setOtpSent(true); setVerified(false); setTimer(OTP_SECONDS);
		setMsg('인증번호를 발송했습니다. 3분 이내 입력해주세요.');
	}
	async function verifyOtp() {
		const r = await authApi.phoneVerify(phone, code, purpose);
		if (!r.success) { setMsg(r.message || '인증 실패'); return; }
		setVerified(true); setMsg('휴대폰 인증이 완료되었습니다.');
		if (tab === 'findId') {
			const f = await authApi.findId(phone);
			setAccounts((f.accounts as typeof accounts) || []);
		}
	}
	async function doReset(e: React.FormEvent) {
		e.preventDefault(); setErr(null);
		if (!verified) return setErr('휴대폰 본인인증을 완료해주세요.');
		if (!loginId.trim()) return setErr('아이디를 입력해주세요.');
		if (!pwOk(pw)) return setErr('비밀번호는 영문·숫자·특수문자를 포함한 8~20자여야 합니다.');
		if (pw !== pw2) return setErr('비밀번호가 일치하지 않습니다.');
		const r = await authApi.resetPassword(loginId.trim(), phone, pw);
		if (!r.success) return setErr(r.message || '재설정에 실패했습니다.');
		setDone(true);
	}

	return (
		<AuthShell eyebrow="EVS MEMBERSHIP" title={<>아이디·비밀번호 찾기</>}
			desc="가입 시 인증한 휴대폰으로 본인 확인 후 아이디를 찾거나 비밀번호를 재설정합니다.">
				<div className={s.links} style={{ marginTop: 0, marginBottom: 20, justifyContent: 'flex-start', gap: 18 }}>
					<a onClick={() => switchTab('findId')} style={{ fontWeight: tab === 'findId' ? 800 : 400, color: tab === 'findId' ? '#00AEEF' : undefined }}>아이디 찾기</a>
					<a onClick={() => switchTab('resetPw')} style={{ fontWeight: tab === 'resetPw' ? 800 : 400, color: tab === 'resetPw' ? '#00AEEF' : undefined }}>비밀번호 재설정</a>
				</div>

				{done ? (
					<>
						<div className={`${s.msg} ${s.success}`}>비밀번호가 재설정되었습니다. 새 비밀번호로 로그인해주세요.</div>
						<button className={s.primary} onClick={() => nav('/login')}>로그인하러 가기</button>
					</>
				) : accounts ? (
					<>
						{accounts.length === 0 ? (
							<div className={`${s.msg} ${s.error}`}>해당 번호로 가입된 계정이 없습니다.</div>
						) : (
							<>
								<div className={`${s.msg} ${s.success}`}>가입된 계정을 확인했습니다.</div>
								{accounts.map((a, i) => (
									<div key={i} className={s.infoRow}>
										<span className="k" style={{ width: 90 }}>계정 {i + 1}</span>
										<span className="v">
											{a.type === 'self' ? `아이디  ${a.loginId}` :
											 a.type === 'social' ? `${a.social} 소셜 로그인 계정` : '이메일 로그인 계정'}
										</span>
									</div>
								))}
							</>
						)}
						<button className={s.primary} style={{ marginTop: 20 }} onClick={() => nav('/login')}>로그인하러 가기</button>
					</>
				) : (
					<form onSubmit={tab === 'resetPw' ? doReset : (e) => e.preventDefault()}>
						{tab === 'resetPw' && (
							<div className={s.field}>
								<label className={s.label}>아이디</label>
								<input className={s.input} value={loginId} onChange={(e) => setLoginId(e.target.value)} placeholder="아이디" />
							</div>
						)}
						<div className={s.field}>
							<label className={s.label}>가입한 휴대폰 번호</label>
							<div className={s.inline}>
								<input className={s.input} value={phone} onChange={(e) => setPhone(e.target.value)}
									placeholder="휴대폰 번호 ('-' 없이)" inputMode="numeric" disabled={verified} />
								<button type="button" className={s.inlineBtn} onClick={sendOtp} disabled={!phone || verified}>
									{otpSent ? '재전송' : '인증 전송'}</button>
							</div>
							{otpSent && !verified && (
								<div className={s.inline} style={{ marginTop: 8 }}>
									<input className={s.input} value={code} onChange={(e) => setCode(e.target.value)}
										placeholder="인증번호 6자리" inputMode="numeric" maxLength={6} />
									<button type="button" className={s.inlineBtn} onClick={verifyOtp} disabled={!code}>확인</button>
								</div>
							)}
							{otpSent && !verified && timer > 0 && <p className={s.hint}>남은 시간 <span className={s.timer}>{mmss(timer)}</span></p>}
							{msg && <p className={`${s.hint} ${verified ? s.ok : s.bad}`}>{msg}</p>}
						</div>

						{tab === 'resetPw' && verified && (
							<>
								<div className={s.field}>
									<label className={s.label}>새 비밀번호</label>
									<input className={s.input} type="password" value={pw} onChange={(e) => setPw(e.target.value)}
										placeholder="영문·숫자·특수문자 포함 8~20자" autoComplete="new-password" />
								</div>
								<div className={s.field}>
									<label className={s.label}>새 비밀번호 확인</label>
									<input className={s.input} type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
										placeholder="새 비밀번호 재입력" autoComplete="new-password" />
								</div>
								{err && <div className={`${s.msg} ${s.error}`}>{err}</div>}
								<button className={s.primary}>비밀번호 재설정</button>
							</>
						)}
						{tab === 'resetPw' && !verified && err && <div className={`${s.msg} ${s.error}`}>{err}</div>}
					</form>
				)}

				<div className={s.links}>
					<Link to="/login">로그인</Link><span className={s.sep}>·</span><Link to="/signup">회원가입</Link>
				</div>
		</AuthShell>
	);
}
